/**
 * `VenueUpcomingShows` — table of upcoming events for a venue. Mirrors the
 * `vn-shows` block in `docs/design-system/1.0.0/templates/city/venue.html`
 * (~lines 293–342): header + per-row date / title+support / badge / price,
 * with a 120ms `padding-left: 8px` hover shift.
 *
 * API-1693 — the header used to read "{meta.total} ON SALE · NEXT 30 DAYS".
 * Every part of that was unbacked: `meta.total` was the page size, the shows
 * query has no 30-day bound (it is unbounded-forward), and nothing in it
 * filters on ticket availability. `GET /venues/:slug/shows` now returns a
 * real count of the set it pages through, so the header states that count and
 * nothing else — no window, no on-sale claim. When the page holds fewer rows
 * than the count, the header says so ("20 OF 41 LISTED") rather than quoting
 * a number the reader cannot reconcile with what is on screen.
 *
 * The empty state is gated on `unavailable`. `fetchVenueShows` manufactures
 * `{ shows: [], meta: { total: 0 } }` for a 404, a non-cursor 400, a 429 or
 * 5xx (WEB-1086), a malformed body and any network or 5s-timeout abort —
 * envelopes that are
 * indistinguishable from a genuinely empty calendar. "Nothing on the calendar
 * yet" is an affirmative claim about the venue, so a degraded fetch renders
 * NOTHING instead: silence is not a claim, and a cold backend must not tell a
 * visitor a 41-show room is dark. That decision is
 * `venueUpcomingShowsHasContent`, which the page template also asks so it can
 * drop its wrapping section (WEB-1106).
 *
 * Server is authoritative for the timezone — every date is rendered using
 * `show.timezone`, never `Intl.DateTimeFormat` against the system clock.
 *
 * Each row links to the cross-host event URL via `cityUrl(cityCode, …)` so
 * marketing-city listings hand off to the app surface for ticketing.
 *
 * WEB-884 — the section pages. The server renders the first
 * {@link VENUE_SHOWS_PAGE_SIZE} shows; "Load more" walks `meta.cursor` from the
 * browser until the API stops handing one back. Before this the page rendered
 * one page and never read the cursor, so API-1693's honest header named shows
 * ("20 OF 41 LISTED") that nothing on the page could reach. The header and the
 * reachable set are now the same set on every venue, at any calendar size.
 *
 * That makes this a client component. The rows still arrive as SSR HTML — a
 * client component is server-rendered on first paint, so crawlers and a
 * JS-disabled visitor see the first page exactly as before; only the tail
 * needs the browser.
 *
 * @since WEB-166
 */
'use client';

import * as React from 'react';
import { Button } from '@/components/atoms/Button';
import { cn } from '@/components/atoms/primitives/utils';
import { cityUrl } from '@/lib/cross-host-urls';
import { VENUE_SHOWS_PAGE_SIZE, fetchVenueShowsPage } from '@/lib/venues/venueShowsPagination';
import type { CityCode } from '@loci/city-editions';
import type { VenueShow, VenueShowsFetchResult, VenueShowBadge } from '@/types/venueDetail';
import { venueUpcomingShowsHasContent } from './venueUpcomingShowsHasContent';

const HEADING_ID = 'venue-upcoming-shows-heading';
const SHOWS_LIST_ID = 'venue-upcoming-shows-list';
const CENTS_PER_DOLLAR = 100;

const USD_WHOLE_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const USD_CENTS_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

type FormattedDate = { dayLine: string; timeLine: string };

const DAY_FORMATTER_CACHE = new Map<string, Intl.DateTimeFormat>();
const TIME_FORMATTER_CACHE = new Map<string, Intl.DateTimeFormat>();

function getDayFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = DAY_FORMATTER_CACHE.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'short',
      month: 'short',
      day: '2-digit',
    });
    DAY_FORMATTER_CACHE.set(timeZone, formatter);
  }
  return formatter;
}

function getTimeFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = TIME_FORMATTER_CACHE.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: 'numeric',
      minute: '2-digit',
    });
    TIME_FORMATTER_CACHE.set(timeZone, formatter);
  }
  return formatter;
}

export function formatVenueShowDate(startsAt: string, timezone: string): FormattedDate {
  const date = new Date(startsAt);
  const dayParts = getDayFormatter(timezone).formatToParts(date);
  const weekday = dayParts.find((p) => p.type === 'weekday')?.value ?? '';
  const month = dayParts.find((p) => p.type === 'month')?.value ?? '';
  const day = dayParts.find((p) => p.type === 'day')?.value ?? '';
  const dayLine = `${weekday.toUpperCase()} · ${month.toUpperCase()} ${day}`;
  const timeLine = getTimeFormatter(timezone).format(date);
  return { dayLine, timeLine };
}

export function formatVenueShowPriceText(priceText: string, priceCents: number | null): string {
  if (priceText.trim().length > 0) return priceText;
  if (priceCents != null) {
    const dollars = priceCents / CENTS_PER_DOLLAR;
    return priceCents % CENTS_PER_DOLLAR === 0
      ? USD_WHOLE_FORMATTER.format(dollars)
      : USD_CENTS_FORMATTER.format(dollars);
  }
  return '-';
}

const BADGE_LABELS: Record<VenueShowBadge, string> = {
  'sold-out': 'SOLD OUT',
  'almost-sold-out': 'ALMOST SOLD',
  curated: 'CURATED',
  new: 'NEW',
};

const BADGE_VARIANT_CLASSES: Record<VenueShowBadge, string> = {
  'sold-out': 'border-red-500 text-red-500',
  'almost-sold-out': 'border-clt-ember text-clt-ember-light',
  curated: 'border-border/60 text-muted-foreground',
  new: 'border-border/60 text-muted-foreground',
};

interface ShowBadgeProps {
  badge: VenueShowBadge;
  priceText: string;
}

function ShowBadge({ badge, priceText }: ShowBadgeProps) {
  // For `almost-sold-out` we want the server-supplied `▲ 90% SOLD` style copy,
  // not the generic label. For everything else, fall back to the canonical
  // label so the rendered string matches the design template.
  const useServerCopy =
    badge === 'almost-sold-out' && priceText.trim().length > 0 ? priceText : BADGE_LABELS[badge];
  return (
    <span
      data-testid="venue-show-badge"
      data-variant={badge}
      className={cn(
        'rounded-full border px-2.5 py-0.5 text-micro uppercase tracking-[0.04em] font-mono',
        BADGE_VARIANT_CLASSES[badge],
      )}
    >
      {useServerCopy}
    </span>
  );
}

export interface VenueUpcomingShowsViewProps {
  cityCode: CityCode;
  venueName: string;
  shows: ReadonlyArray<VenueShow>;
  total: number;
  /**
   * The envelope is a degraded fallback rather than a reading of the
   * calendar — the fetch 404'd, 400'd, returned an unparseable body, or timed
   * out. Suppresses the empty state, which would otherwise state as fact
   * something the request never established.
   */
  unavailable?: boolean;
  /**
   * The API handed back another page. Presence of a handler is what renders
   * the affordance; `hasMore` is what decides whether there is anything left
   * to ask for (WEB-884).
   */
  hasMore?: boolean;
  /** A cursor page is in flight — the control is busy, not absent. */
  isLoadingMore?: boolean;
  /**
   * The last cursor page failed. The rows already on screen stay; the control
   * offers a retry and says so, rather than going quiet and reading as "that
   * was the end of the calendar".
   */
  loadMoreFailed?: boolean;
  onLoadMore?: () => void;
  className?: string;
}

/**
 * Header meta for the count. `null` suppresses the count entirely, which is
 * what an empty calendar gets — "0 LISTED" is a number nobody asked for.
 *
 * `rendered < listed` means the response paged: say both numbers so the count
 * and the visible rows reconcile instead of silently disagreeing.
 */
export function formatVenueShowsCount(rendered: number, listed: number): string | null {
  if (listed <= 0) return null;
  return rendered < listed ? `${rendered} OF ${listed} LISTED` : `${listed} LISTED`;
}

/**
 * Copy for the "Load more" control (WEB-884).
 *
 * The count is `min(remaining, page size)` so the button promises exactly what
 * the next request can deliver. `remaining` can be 0 while the API still holds
 * a cursor — `meta.total` is a snapshot and the calendar moves under it — so a
 * non-positive remainder falls back to the unnumbered label rather than
 * offering to load zero shows.
 */
export function formatVenueShowsLoadMoreLabel(remaining: number): string {
  if (remaining <= 0) return 'Load more shows';
  const next = Math.min(remaining, VENUE_SHOWS_PAGE_SIZE);
  return `Load ${next} more show${next === 1 ? '' : 's'}`;
}

export function VenueUpcomingShowsView({
  cityCode,
  venueName,
  shows,
  total,
  unavailable = false,
  hasMore = false,
  isLoadingMore = false,
  loadMoreFailed = false,
  onLoadMore,
  className,
}: VenueUpcomingShowsViewProps): React.ReactElement | null {
  const rendered = shows.length;
  // A stale cached envelope could carry a `total` below the rows it shipped.
  // Clamping up keeps the header from claiming fewer shows than are on screen.
  const listed = Math.max(total, rendered);
  const countLabel = formatVenueShowsCount(rendered, listed);
  // A failed page keeps the control on screen so the visitor can retry; without
  // that, an error would hide the only route to the shows the header counts.
  const showLoadMore = onLoadMore !== undefined && (hasMore || loadMoreFailed);

  // We did not learn the calendar is empty — we failed to read it. Say nothing.
  if (!venueUpcomingShowsHasContent({ shows, unavailable })) return null;

  if (rendered === 0 && listed === 0) {
    return (
      <section
        role="region"
        aria-labelledby={HEADING_ID}
        data-testid="venue-upcoming-shows"
        data-state="empty"
        className={cn('mb-8 border-b border-border/60 pb-8', className)}
      >
        <h2
          id={HEADING_ID}
          className="m-0 mb-4 text-2xl font-bold tracking-tight text-foreground font-display sm:text-3xl"
        >
          Upcoming shows
        </h2>
        <p
          data-testid="venue-upcoming-shows-empty"
          className="m-0 border-t border-border/60 pt-4 text-xs tracking-[0.08em] text-muted-foreground font-mono"
        >
          Nothing on the calendar yet
        </p>
      </section>
    );
  }

  return (
    <section
      role="region"
      aria-labelledby={HEADING_ID}
      data-testid="venue-upcoming-shows"
      className={cn('mb-8 border-b border-border/60 pb-8', className)}
    >
      <h2
        id={HEADING_ID}
        className="m-0 mb-4 text-2xl font-bold tracking-tight text-foreground font-display sm:text-3xl"
      >
        Upcoming shows
        {countLabel ? (
          <small className="ml-3 inline-block text-xs font-medium tracking-[0.08em] text-accent-text font-mono">
            · {countLabel}
          </small>
        ) : null}
      </h2>
      <div id={SHOWS_LIST_ID} className="flex flex-col">
        {shows.map((show) => {
          const date = formatVenueShowDate(show.startsAt, show.timezone);
          const priceLabel = formatVenueShowPriceText(show.priceText, show.priceCents);
          const href = cityUrl(cityCode, `/event/${show.eventSlug}`);
          // Shows API maps Event.description onto supportText. Clamp overflow
          // only; the row already links to event detail for the full field (WEB-922).
          const bodyCopy = show.supportText?.trim() ?? '';
          return (
            <a
              key={show.eventSlug}
              href={href}
              aria-label={`${show.title} · ${date.dayLine}`}
              data-testid="venue-show-row"
              className={cn(
                'grid items-center gap-x-4 gap-y-1 border-b border-border/60 py-4 no-underline last:border-b-0',
                // WEB-920: 80px was the design-template first track, but
                // `WED · SEP 16` at text-base display wraps before the day
                // number. Size the column to the date token and keep that
                // token nowrap so the day never drops under weekday/month.
                // `minmax(0,1fr)` lets the title shrink. Below `sm`, badge
                // and price wrap under the title so date + autos + gaps
                // cannot overflow the venue column.
                'grid-cols-[max-content_minmax(0,1fr)]',
                'sm:grid-cols-[max-content_minmax(0,1fr)_auto_auto] sm:gap-4',
                // Tap target ≥ 44px — rows are the primary mobile entry
                // point into individual event detail pages.
                'min-h-11 desktop:min-h-0',
                // Hover-shift transition is decorative — disable under
                // `prefers-reduced-motion: reduce` per WCAG 2.3.3 + WEB-173.
                // `motion-safe:` only adds the transition when the user
                // has not requested reduced motion. Without the `motion-safe:`
                // prefix, no hover-shift is applied — layout stays static.
                'motion-safe:transition-[padding] motion-safe:duration-[120ms]',
                'motion-safe:hover:pl-2',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              )}
            >
              {/*
                `suppressHydrationWarning` because these strings come out of
                `Intl.DateTimeFormat`, whose output is ICU-version dependent —
                ICU 72 changed the separator before AM/PM from U+0020 to
                U+202F. Server and browser can therefore disagree by one
                invisible codepoint on every row. Harmless before WEB-884
                (this section never hydrated); a console mismatch on every show
                once it became a client component. The value itself is
                server-authoritative either way: the timezone is `show.timezone`,
                never the reader's clock.
              */}
              <div
                suppressHydrationWarning
                data-testid="venue-show-date"
                className="whitespace-nowrap text-base font-semibold leading-tight tracking-tight text-foreground font-display"
              >
                <span suppressHydrationWarning data-testid="venue-show-date-token">
                  {date.dayLine}
                </span>
                <small
                  suppressHydrationWarning
                  className="mt-0.5 block text-micro tracking-[0.08em] text-accent-text font-mono"
                >
                  {date.timeLine}
                </small>
              </div>
              <div data-testid="venue-show-title" className="min-w-0 overflow-hidden">
                <h3 className="m-0 wrap-break-word text-base font-semibold text-foreground font-display">
                  {show.title}
                </h3>
                {bodyCopy.length > 0 ? (
                  <div
                    data-testid="venue-show-body"
                    className="mt-0.5 line-clamp-2 text-micro tracking-[0.04em] text-muted-foreground font-mono"
                  >
                    {bodyCopy}
                  </div>
                ) : null}
              </div>
              <div
                data-testid="venue-show-meta"
                className="col-span-2 flex min-w-0 flex-wrap items-center justify-end gap-x-4 gap-y-1 sm:contents"
              >
                {show.badge ? (
                  <ShowBadge badge={show.badge} priceText={show.priceText} />
                ) : (
                  <span className="hidden sm:block" />
                )}
                <div className="shrink-0 text-right text-base font-semibold text-foreground font-display">
                  {priceLabel}
                </div>
              </div>
            </a>
          );
        })}
      </div>
      {showLoadMore ? (
        <div className="flex flex-col items-center gap-2 pt-6">
          {loadMoreFailed ? (
            <p
              role="alert"
              data-testid="venue-upcoming-shows-load-more-error"
              className="m-0 text-xs tracking-[0.08em] text-muted-foreground font-mono"
            >
              Couldn&apos;t load the rest of the calendar.
            </p>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            onClick={onLoadMore}
            disabled={isLoadingMore}
            aria-controls={SHOWS_LIST_ID}
            data-testid="venue-upcoming-shows-load-more"
          >
            {isLoadingMore
              ? 'Loading'
              : loadMoreFailed
                ? 'Try again'
                : formatVenueShowsLoadMoreLabel(listed - rendered)}
          </Button>
        </div>
      ) : null}
      {/*
        Announced, not just present (WEB-884): appending rows is a silent DOM
        mutation for a screen-reader user, so the reconciliation line doubles as
        the live region that reports how far through the calendar they now are.
      */}
      <p className="sr-only" aria-live="polite" aria-hidden={false}>
        {rendered < listed
          ? `${venueName} · showing ${rendered} of ${listed} upcoming shows`
          : `${venueName} · ${listed} upcoming show${listed === 1 ? '' : 's'}`}
      </p>
    </section>
  );
}

export interface VenueUpcomingShowsProps {
  cityCode: CityCode;
  venueName: string;
  /** Venue slug — the cursor walk needs it to request the next page. */
  slug: string;
  data: VenueShowsFetchResult;
  className?: string;
}

/**
 * Append `incoming` to `existing`, dropping any show already on screen.
 *
 * Cursor pagination on `(date, id)` should not repeat a row, but `eventSlug` is
 * this list's React key: one repeat under a re-issued cursor would throw a
 * duplicate-key error and corrupt reconciliation. Cheap insurance against a
 * crash whose cause would be invisible in the rendered output.
 */
function appendNewShows(
  existing: ReadonlyArray<VenueShow>,
  incoming: ReadonlyArray<VenueShow>,
): ReadonlyArray<VenueShow> {
  const seen = new Set(existing.map((show) => show.eventSlug));
  const additions = incoming.filter((show) => !seen.has(show.eventSlug));
  return additions.length > 0 ? [...existing, ...additions] : existing;
}

/**
 * Connected component — owns the cursor walk (WEB-884).
 *
 * `data` is the server-rendered first page. Subsequent pages are fetched in the
 * browser (see `lib/venues/venueShowsPagination.ts` for why the request does not
 * go through this app's server) and accumulated in state, so the cursor never
 * reaches the URL: a visitor deep-linking `/venue/<slug>` gets the same first
 * page whether or not someone else clicked through the tail.
 *
 * `meta.total` is re-read off each page rather than pinned to the first. The
 * count is a live COUNT of an unbounded-forward set, so a show can be added or
 * pass its start time mid-walk; trusting the newest reading keeps the header
 * reconciling against the rows on screen instead of against a stale snapshot.
 *
 * **Callers MUST key this component on `slug`.** The accumulated tail is local
 * state, so an instance reused across two venues would otherwise render venue
 * A's page 2 under venue B's heading — and, because a `(date, id)` cursor
 * carries no venue, would then walk A's calendar from B's page. Resetting the
 * state during render would not be enough on its own: it cannot cancel an
 * in-flight request, whose `.then` would land on the new venue moments later.
 * A `key` solves both at once — React unmounts, the cleanup below aborts, and
 * the new instance starts from the new server page. `VenueDetailTemplate` does
 * this; a new call site must too.
 *
 * The one state a completed walk can still leave is `rendered < listed` with no
 * cursor: the dedup below dropped a repeat, or keyset pagination stepped over a
 * row ingest inserted behind the cursor while the reader was walking. The
 * header keeps saying both numbers there rather than quietly restating the
 * smaller one — "50 OF 53 LISTED" is the honest reading of a set that moved,
 * and a reload re-seeds it. What it must never become is the bare "53 LISTED"
 * over 50 rows, which is the disagreement API-1693 removed.
 */
export function VenueUpcomingShows({
  cityCode,
  venueName,
  slug,
  data,
  className,
}: VenueUpcomingShowsProps): React.ReactElement | null {
  const [shows, setShows] = React.useState<ReadonlyArray<VenueShow>>(data.shows);
  const [total, setTotal] = React.useState<number>(data.meta.total);
  const [cursor, setCursor] = React.useState<string | null>(data.meta.cursor);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = React.useState(false);

  const abortRef = React.useRef<AbortController | null>(null);
  React.useEffect(() => () => abortRef.current?.abort(), []);

  const handleLoadMore = React.useCallback(() => {
    if (cursor === null || isLoadingMore) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setIsLoadingMore(true);
    setLoadMoreFailed(false);

    void fetchVenueShowsPage(slug, cursor, { signal: controller.signal })
      .then((page) => {
        if (controller.signal.aborted) return;
        setShows((current) => appendNewShows(current, page.shows));
        setTotal(page.meta.total);
        setCursor(page.meta.cursor);
        setIsLoadingMore(false);
      })
      .catch((error: unknown) => {
        // An abort is our own teardown, not a failure to report.
        if (controller.signal.aborted) return;
        console.warn(`[VenueUpcomingShows] Failed to load more shows for ${slug}:`, error);
        setLoadMoreFailed(true);
        setIsLoadingMore(false);
      });
  }, [cursor, isLoadingMore, slug]);

  return (
    <VenueUpcomingShowsView
      cityCode={cityCode}
      venueName={venueName}
      shows={shows}
      total={total}
      unavailable={data.unavailable ?? false}
      hasMore={cursor !== null}
      isLoadingMore={isLoadingMore}
      loadMoreFailed={loadMoreFailed}
      onLoadMore={handleLoadMore}
      className={className}
    />
  );
}
